import PropTypes from "prop-types";
import SBINModal from "./SBINModal";
import { BTN_OUTLINE } from "../../ui/modalStyles";
import { formatInTimeZone } from "../../utils/datetime";

function humanizeLogAction(action) {
  if (!action) return "";
  return String(action)
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function formatApptLogWhat(log) {
  if (!log) return "--";
  const map = {
    scheduled: "Scheduled",
    create: "Scheduled",
    checked_in: "Checked In",
    check_in: "Checked In",
    undo_check_in: "Undo Check-In",
    rescheduled: "Rescheduled",
    appointment_edited: "Appointment Edited",
    email_reminder_sent: "Email Reminder sent",
    ticket_closed: "Ticket Closed",
    ticket_deleted: "Ticket Deleted",
    ticket_reopened: "Ticket Re-opened",
    confirm: "Confirmed",
    unconfirm: "Unconfirmed",
    left_message: "Left Message",
    cancel: "Cancelled",
    confirm_visit: "Visit Confirmed",
    unconfirm_visit: "Visit Unconfirmed",
  };
  const label = map[log.action] || humanizeLogAction(log.action) || "Update";
  const message = (log.message || "").trim();
  if (!message) return label;
  return `${label} - ${message}`;
}

function formatApptLogWho(log) {
  if (!log) return "--";
  if (log.created_by_name) return log.created_by_name;
  if (log.created_by_id != null) return `User #${log.created_by_id}`;
  return "System";
}

function formatApptLogWhen(log, salonTimezone) {
  if (!log) return "--";
  if (log.display_when) return log.display_when;
  const effectiveTimezone = log.salon_timezone || salonTimezone;
  if (log.created_at) {
    if (effectiveTimezone) {
      const formatted = formatInTimeZone(
        log.created_at,
        effectiveTimezone,
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        },
        "en-CA"
      );
      if (formatted && formatted !== "-") {
        return formatted;
      }
    }
    return new Date(log.created_at).toLocaleString();
  }
  return "--";
}

export default function AppointmentLogModal({
  isOpen,
  onClose,
  logs = [],
  loading = false,
  error = "",
  title = "Appointment Log",
  salonTimezone = "",
}) {
  if (!isOpen) return null;

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="xxl"
      footer={
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className={BTN_OUTLINE}>
            Close
          </button>
        </div>
      }
    >
      {error ? (
        <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm md:text-base text-amber-800">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200">
        <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 bg-slate-50 px-4 py-2 text-sm md:text-base font-semibold uppercase tracking-wide text-slate-500">
          <span>What</span>
          <span>Who</span>
          <span>When</span>
        </div>

        <div className="divide-y divide-slate-200">
          {loading ? (
            <div className="px-4 py-3 text-sm md:text-base text-slate-500">
              Loading...
            </div>
          ) : logs.length === 0 ? (
            <div className="px-4 py-3 text-sm md:text-base text-slate-500">
              No log entries.
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id ?? `${log.action}-${log.created_at}-${Math.random()}`}
                className="grid grid-cols-[2fr_1fr_1fr] gap-2 px-4 py-3 text-sm md:text-base text-slate-700"
              >
                <span className="whitespace-nowrap overflow-hidden text-ellipsis text-slate-900">
                  {formatApptLogWhat(log)}
                </span>
                <span className="whitespace-nowrap">{formatApptLogWho(log)}</span>
                <span className="whitespace-nowrap">
                  {formatApptLogWhen(log, salonTimezone)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </SBINModal>
  );
}

AppointmentLogModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  logs: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  error: PropTypes.string,
  title: PropTypes.string,
  salonTimezone: PropTypes.string,
};
